import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Badge, Card, PageHeader } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { SortableTable, type SortableColumn } from '@/components/ui/SortableTable';
import { fetchSeoDashboardSummary, fetchSeoRecommendedActions } from '@/data/sources-seod';
import type { SeoMovementState, SeoRecommendedAction } from '@/types/seod';
import { formatRelative } from '@/lib/utils';
import { useActiveDomain } from '@/hooks/useActiveDomain';
import { scopeLabel } from '@/pages/seo/domain-scope';

/* /seo/executive — SEO Executive Dashboard (W4a). The launch-critical executive
 * snapshot: ranking gains/losses over a window, open-alert count, and a
 * prioritised, evidence-backed recommended-actions list.
 *
 * HONESTY + SAFETY INVARIANTS (W4a contract):
 *  - FREE + read-only over already-stored, workspace-scoped rows
 *    (bc_seo_alerts + bc_seo_rankings). NO provider call, NO charge — so both
 *    reads AUTO-LOAD on mount (no "click to fetch" spend guard needed).
 *  - Counts are honest: a zero gain/loss/open-alert is rendered as 0, never as
 *    absent. Every recommended action's text is derived server-side from the
 *    alert's own stored evidence — never invented, never a fresh LLM call.
 *  - NO proprietary visibility index, NO unsupported metric — gains/losses are
 *    literal counts of the AlertEngine's own locked alert types.
 *  - Source-attributed + last-updated; honest loading / error / empty states.
 */

const WINDOW_DAYS = 30;

function severityTone(sev: string): 'neutral' | 'warning' | 'danger' | 'success' {
  const s = sev.toLowerCase();
  if (s === 'critical' || s === 'high' || s === 'error') return 'danger';
  if (s === 'warning' || s === 'medium') return 'warning';
  if (s === 'info' || s === 'low') return 'neutral';
  return 'neutral';
}

/** Position evidence: "6 → 10" honestly, em-dash for any missing side. */
function movement(previous: number | null, current: number | null): string {
  const p = previous === null ? '—' : String(previous);
  const c = current === null ? '—' : String(current);
  if (previous === null && current === null) return '—';
  return `${p} → ${c}`;
}

const ACTION_COLUMNS: SortableColumn<SeoRecommendedAction>[] = [
  {
    key: 'severity',
    header: 'Severity',
    sortable: true,
    sortValue: (a) => a.severity,
    render: (a) => <Badge tone={severityTone(a.severity)}>{a.severity || '—'}</Badge>,
    cellClassName: 'whitespace-nowrap',
  },
  {
    key: 'title',
    header: 'Alert',
    sortable: true,
    sortValue: (a) => a.title,
    render: (a) => a.title || '—',
    truncate: true,
    maxWidthClass: 'max-w-[260px]',
    title: (a) => a.title,
    cellClassName: 'font-medium text-text-primary',
  },
  {
    key: 'type',
    header: 'Type',
    sortable: true,
    sortValue: (a) => a.alert_type,
    render: (a) => <span className="font-mono text-xs text-text-tertiary">{a.alert_type}</span>,
    cellClassName: 'whitespace-nowrap',
  },
  {
    key: 'keyword',
    header: 'Keyword',
    render: (a) => a.evidence.keyword || '—',
    truncate: true,
    maxWidthClass: 'max-w-[180px]',
    title: (a) => a.evidence.keyword,
  },
  {
    key: 'movement',
    header: 'Position',
    align: 'right',
    render: (a) => (
      <span className="font-mono text-text-secondary">
        {movement(a.evidence.previous, a.evidence.current)}
      </span>
    ),
    cellClassName: 'whitespace-nowrap',
  },
  {
    key: 'action',
    header: 'Recommended action',
    render: (a) => <span className="text-text-secondary">{a.recommended_action}</span>,
    truncate: true,
    maxWidthClass: 'max-w-[360px]',
    title: (a) => a.recommended_action,
  },
  {
    key: 'created_at',
    header: 'Raised',
    sortable: true,
    sortValue: (a) => a.created_at,
    render: (a) => (a.created_at ? formatRelative(a.created_at) : '—'),
    cellClassName: 'whitespace-nowrap text-text-tertiary',
  },
];

/** Honest movement-tile copy for the 3-way movement_state (owner 2026-07-21):
 *  distinguish missing data / insufficient history from a true zero so the
 *  gains/losses tiles never render '0' as if it were a real zero when it is not.
 *  Returns null for 'ok' (a genuine 0 is honest) and 'workspace' (spans
 *  domains) -> the caller renders the real count. */
function movementMissing(state: SeoMovementState): { label: string; sub: string } | null {
  if (state === 'no_data') {
    return {
      label: 'No data yet for this domain',
      sub: 'Cache rankings for this domain to compare gains and losses',
    };
  }
  if (state === 'insufficient_history') {
    return {
      label: 'Not enough ranking history yet',
      sub: 'Needs a second dated ranking snapshot before gains/losses can be compared',
    };
  }
  return null;
}

/** One executive snapshot tile — a labelled count with an honest sub-line. When
 *  `missing` is set, the tile shows that honest message instead of a number, so
 *  a missing / insufficient movement figure is never rendered as a bare '0'. */
function StatTile({
  label,
  value,
  sub,
  tone,
  missing,
}: {
  label: string;
  value: number;
  sub: string;
  tone: 'gain' | 'loss' | 'neutral';
  missing?: string | null;
}) {
  const isMissing = typeof missing === 'string' && missing.length > 0;
  const valueColor = isMissing
    ? 'text-text-tertiary'
    : tone === 'gain'
      ? 'text-success'
      : tone === 'loss'
        ? 'text-danger'
        : 'text-text-primary';
  return (
    <Card className="p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-text-tertiary">{label}</div>
      {isMissing ? (
        <div className={`mt-1 text-base font-medium ${valueColor}`}>{missing}</div>
      ) : (
        <div className={`mt-1 font-mono text-3xl font-semibold ${valueColor}`} data-tabular>
          {value.toLocaleString('en-GB')}
        </div>
      )}
      <div className="mt-1 text-xs text-text-tertiary">{sub}</div>
    </Card>
  );
}

export function SeoExecutiveDashboardPage() {
  const navigate = useNavigate();
  // Scope EVERY figure to the SELECTED 'Viewing' domain so this domain-level
  // dashboard never shows another domain's data (owner 2026-07-21). No domain
  // selected -> honest workspace-wide lens.
  const { active } = useActiveDomain();
  const projectId = active?.projectId ?? null;

  // FREE read-only surfaces — safe to auto-load on mount.
  const summaryQ = useQuery({
    queryKey: ['seo', 'dashboard-summary', WINDOW_DAYS, projectId],
    queryFn: () => fetchSeoDashboardSummary(WINDOW_DAYS, projectId),
  });
  const actionsQ = useQuery({
    queryKey: ['seo', 'recommended-actions', 'open', projectId],
    queryFn: () => fetchSeoRecommendedActions('open', 50, projectId),
  });

  const summary = summaryQ.data ?? null;
  const actions = actionsQ.data?.actions ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="SEO Executive Dashboard"
        subtitle="Your at-a-glance SEO snapshot — ranking gains and losses, open alerts and prioritised, evidence-backed actions. Read straight from stored, source-attributed data; free to view and never a proprietary-index claim."
      />

      {/* --- Snapshot tiles ------------------------------------------------ */}
      {summaryQ.isPending ? (
        <LoadingState rows={2} />
      ) : summaryQ.error ? (
        <ErrorState error={summaryQ.error} onRetry={() => void summaryQ.refetch()} />
      ) : summary ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="neutral">Source: {summary.source}</Badge>
            <Badge tone="neutral">{scopeLabel(active)}</Badge>
            <span className="text-xs text-text-tertiary">
              Last {WINDOW_DAYS} days ·{' '}
              {summary.fetched_at
                ? `updated ${formatRelative(summary.fetched_at)}`
                : 'update time not reported'}
            </span>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {(() => {
              // HONEST movement tiles (owner 2026-07-21): the backend's
              // movement_state distinguishes a true zero from missing data /
              // insufficient history. Never render '0' for no_data or
              // insufficient_history — that is the forbidden "0-as-missing".
              const mm = movementMissing(summary.movement_state);
              return (
                <>
                  <StatTile
                    label="Ranking gains"
                    value={summary.gains}
                    sub={mm ? mm.sub : `${summary.gains_detail.entered_top10} entered the top 10`}
                    tone={mm ? 'neutral' : summary.gains > 0 ? 'gain' : 'neutral'}
                    missing={mm?.label ?? null}
                  />
                  <StatTile
                    label="Ranking losses"
                    value={summary.losses}
                    sub={mm ? mm.sub : `${summary.losses_detail.rank_drop} rank drop${summary.losses_detail.rank_drop === 1 ? '' : 's'} · ${summary.losses_detail.left_top10} left top 10`}
                    tone={mm ? 'neutral' : summary.losses > 0 ? 'loss' : 'neutral'}
                    missing={mm?.label ?? null}
                  />
                </>
              );
            })()}
            <StatTile
              label="Open alerts"
              value={summary.open_alerts}
              sub="Awaiting review or resolution"
              tone="neutral"
            />
            <StatTile
              label="Tracked keywords"
              value={summary.tracked_keywords}
              sub={`Across ${summary.tracked_projects} ${summary.tracked_projects === 1 ? 'project' : 'projects'}`}
              tone="neutral"
            />
          </div>

          {/* --- Ranked-organic snapshot (SEO-WIRING) --------------------- */}
          {/* The SAME cached ranked-organic dataset (and shared selector) that
              powers the Opportunities panel + Aura. Replaces reliance on the
              tracked-keyword count for the organic picture. Honest zeros when
              there is no unexpired cache (Refresh under SEO would spend credits). */}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <span className="text-sm font-medium text-text-primary">Ranked organic</span>
            <Badge tone="success">Real keywords you rank for</Badge>
            {summary.organic.refresh_gated && summary.organic.total_organic_keywords === 0 && (
              <Badge tone="warning">
                {active
                  ? 'No data yet for this domain — fetch organic keywords to populate'
                  : 'No cached data — fetch organic keywords to populate'}
              </Badge>
            )}
            <span className="ml-auto text-xs text-text-tertiary">
              Source: {summary.organic.source}
              {summary.organic.fetched_at ? ` · updated ${formatRelative(summary.organic.fetched_at)}` : ''}
            </span>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <StatTile
              label="Organic keywords"
              value={summary.organic.total_organic_keywords}
              sub="Cached keywords your site ranks for"
              tone={summary.organic.total_organic_keywords > 0 ? 'gain' : 'neutral'}
            />
            <StatTile
              label="Page-one keywords"
              value={summary.organic.page_one_keywords}
              sub="Ranking in positions 1–10"
              tone={summary.organic.page_one_keywords > 0 ? 'gain' : 'neutral'}
            />
            <StatTile
              label="Striking distance"
              value={summary.organic.striking_distance_keywords}
              sub="Positions 4–20 — close to page one"
              tone="neutral"
            />
          </div>
          {summary.organic.top_actions.length > 0 && (
            <Card className="p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-text-primary">Top ranked-organic actions</span>
                <Badge tone="success">Evidence-scored · cached · no paid refresh</Badge>
                <button
                  type="button"
                  onClick={() => navigate('/seo/opportunities')}
                  className="ml-auto text-xs text-ai underline-offset-2 hover:underline"
                >
                  See all opportunities
                </button>
              </div>
              <ol className="mt-3 space-y-2">
                {summary.organic.top_actions.map((a, i) => (
                  <li key={`${a.keyword}-${i}`} className="rounded-lg border border-border-subtle bg-surface p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-text-primary">{a.keyword || '—'}</span>
                      {a.position !== null && (
                        <Badge tone="neutral">position {a.position}</Badge>
                      )}
                      {a.score !== null && (
                        <Badge tone={a.score >= 70 ? 'success' : 'warning'}>score {a.score}</Badge>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-text-secondary">{a.action || '—'}</p>
                  </li>
                ))}
              </ol>
            </Card>
          )}
        </>
      ) : null}

      {/* --- Recommended actions ------------------------------------------- */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-text-primary">Recommended actions</span>
          <Badge tone="neutral">Open alerts</Badge>
          <Badge tone="neutral">{scopeLabel(active)}</Badge>
          <span className="ml-auto text-xs text-text-tertiary">
            Evidence-grounded · deterministic · never an invented recommendation
          </span>
        </div>
        <p className="mt-1 text-sm text-text-secondary">
          Each action is derived from an alert's own stored evidence (keyword and previous/current
          position). Sortable by severity, type or date — sorting is free and never reloads. When a domain is selected above, these actions are scoped to it; otherwise they span every connected domain.
        </p>

        <div className="mt-4">
          {actionsQ.isPending ? (
            <LoadingState rows={4} />
          ) : actionsQ.error ? (
            <ErrorState error={actionsQ.error} onRetry={() => void actionsQ.refetch()} />
          ) : actions.length === 0 ? (
            <EmptyState
              title="No open actions right now"
              teach="When the scheduler raises an alert — a ranking drop, a top-10 exit or a new top-10 entry — it appears here with a plain-English recommended action grounded in the stored evidence. An empty list is a healthy list."
            />
          ) : (
            <div className="overflow-hidden rounded-lg border border-border-subtle bg-surface">
              <SortableTable
                rows={actions}
                columns={ACTION_COLUMNS}
                getRowKey={(a) => `action-${a.alert_id}`}
                minWidthClass="min-w-[860px]"
                caption="Prioritised, evidence-backed recommended SEO actions"
              />
              <div className="flex flex-wrap items-center gap-x-2 border-t border-border-subtle px-3 py-2 text-[11px] text-text-tertiary">
                <Badge tone="neutral">Source: {actionsQ.data?.source ?? 'bc_seo_alerts'}</Badge>
                <span>
                  {actions.length} open {actions.length === 1 ? 'action' : 'actions'} ·{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/seo/alerts')}
                    className="text-ai underline-offset-2 hover:underline"
                  >
                    Open the alerts inbox
                  </button>{' '}
                  ·{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/seo/opportunities')}
                    className="text-ai underline-offset-2 hover:underline"
                  >
                    See opportunities
                  </button>
                </span>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
